import React, {
  useEffect,
  useCallback,
  useContext,
  useRef,
  RefObject,
} from 'react';
import cx from 'classnames';
import { useDispatch } from 'react-redux';

import {
  VideoRecorder,
  VideoRecorderProps,
  RecorderState,
  Thumbnail,
  RecordingMetadata,
  RecorderControl,
} from '@vimily/bonjoro-ui-components-v2/VideoRecorder';

import RespondentContext from '~/Share/TestimonialLandingPage/Context/Respondent';
import * as Actions from '~/App/Components/Record/Stores/Recorder/Actions';
import useAnalytics from '~/react-hooks/use-analytics';
import {
  TESTIMONIAL_RESPONSE_RECORD_DELETE,
  TESTIMONIAL_RESPONSE_RECORD_REDO,
  TESTIMONIAL_RESPONSE_RECORD_SETTINGS,
  TESTIMONIAL_RESPONSE_RECORD_SETTINGS_SAVE,
  TESTIMONIAL_RESPONSE_RECORD_REPLAY,
  TESTIMONIAL_RESPONSE_RECORD_STOP,
  TESTIMONIAL_RESPONSE_RECORD_START,
} from '~/generated/AnalyticsTypes';
import {
  useAudioDevice,
  useVideoDevice,
  useAudioDevices,
  useVideoDevices,
} from '~/App/Api/Devices';
import useStepContext from '../../Typeform/useStepContext';
import { useMutation } from 'react-query';
import { updateRespondentMetadata } from '~/Share/TestimonialLandingPage/Api/TestimonialRespondent';

const rootDimensionsClassNames = [
  'v2-min-h-[min(100vw,100vh)] v2-min-w-[min(100vw,100vh)]',
  'md:v2-min-h-0 md:v2-min-w-0',
];

export interface RecorderProps {
  onBackClick: () => void;
  manualUpload?: File;
  showBonjoroBadge?: boolean;
  testimonialUuid: string;
  recorderControlRef?: RefObject<RecorderControl>;
  recorderState: RecorderState;
  onRecorderStateChange?: (state: RecorderState) => void;
}

const createDeviceLabelArray = (deviceArray: MediaDeviceInfo[]) => {
  return deviceArray.map(({ label }) => {
    return label;
  });
};

export default function Recorder({
  onBackClick,
  manualUpload,
  showBonjoroBadge = false,
  testimonialUuid,
  recorderControlRef,
  recorderState,
  onRecorderStateChange,
}: RecorderProps) {
  const recordQualityRef = useRef<string | undefined>();
  const analytics = useAnalytics();

  const { respondent } = useContext(RespondentContext);
  const respondentUuid = respondent?.get('uuid') as string;

  const { data: audioDevice } = useAudioDevice();
  const { data: videoDevice } = useVideoDevice();
  const { data: videoDevices = [] } = useVideoDevices();
  const { data: audioDevices = [] } = useAudioDevices();

  const { isCurrent } = useStepContext();

  const dispatch = useDispatch();

  const { mutateAsync: updateRespondentMetadataMutation } = useMutation(
    updateRespondentMetadata
  );

  const createMeta = (audioDevice?: string, videoDevice?: string) => {
    const audioDeviceLabels = createDeviceLabelArray(audioDevices);
    const videoDeviceLabels = createDeviceLabelArray(videoDevices);

    const meta = {
      recordResponse: {
        audio: audioDevice,
        video: videoDevice,
        audioDevices: audioDeviceLabels,
        videoDevices: videoDeviceLabels,
        recordQuality: recordQualityRef.current,
      },
    };

    return JSON.stringify(meta);
  };

  const updateMetadata = useCallback((metadata: string) => {
    updateRespondentMetadataMutation({
      testimonialUuid,
      respondentUuid,
      metadata: metadata,
    });
  }, []);

  useEffect(() => {
    if (!audioDevice?.label || !videoDevice?.label) {
      return;
    }

    updateMetadata(createMeta(audioDevice?.label, videoDevice?.label));
  }, [audioDevice?.label, videoDevice?.label, recordQualityRef.current]);

  function handleRecordingComplete(
    data: Blob,
    thumbnails: Thumbnail[],
    metadata?: RecordingMetadata
  ) {
    dispatch(Actions.setBlob(data));
    dispatch(Actions.setThumbnails(thumbnails));
    metadata?.duration && dispatch(Actions.setDuration(metadata.duration));
  }

  useEffect(() => {
    if (
      !isCurrent &&
      recorderState !== RecorderState.Complete &&
      recorderState !== RecorderState.Recording &&
      !manualUpload
    ) {
      onBackClick();
    }
  }, [isCurrent]);

  const handleOnResetClick = () => {
    onRecorderStateChange?.(RecorderState.Init);
    if (manualUpload) {
      onBackClick();
    }
    analytics.track(TESTIMONIAL_RESPONSE_RECORD_REDO);
  };

  const handleBackClick = () => {
    onBackClick();
    if (recorderState === RecorderState.Complete) {
      analytics.track(TESTIMONIAL_RESPONSE_RECORD_DELETE);
    }
  };

  const handleSettingsClick = () => {
    analytics.track(TESTIMONIAL_RESPONSE_RECORD_SETTINGS);
  };

  const handleSettingsClose = useCallback<
    Exclude<VideoRecorderProps['onSettingsClose'], undefined>
  >(
    (videoDevice, audioDevice) => {
      updateMetadata(createMeta(audioDevice?.label, videoDevice?.label));
      analytics.track(TESTIMONIAL_RESPONSE_RECORD_SETTINGS_SAVE, {
        camera: videoDevice?.label,
        audio: audioDevice?.label,
      });
    },
    [videoDevice, audioDevice]
  );

  useEffect(() => {
    switch (recorderState) {
      case RecorderState.Playing:
        analytics.track(TESTIMONIAL_RESPONSE_RECORD_REPLAY);
        break;
      case RecorderState.Complete:
        analytics.track(TESTIMONIAL_RESPONSE_RECORD_STOP);
        break;
      case RecorderState.Recording:
        analytics.track(TESTIMONIAL_RESPONSE_RECORD_START);
        break;
    }
  }, [recorderState]);

  const handleVideoRecorderInit = (recordQuality) => {
    recordQualityRef.current = recordQuality as string;
  };

  return (
    <div
      className={cx(
        'bg-white left-0 top-0 z-40 w-full md:h-full md:relative md:rounded-none overflow-hidden',
        rootDimensionsClassNames
      )}
    >
      <VideoRecorder
        ref={recorderControlRef}
        height='100%'
        width='100%'
        manualUpload={manualUpload}
        onRecorderStateChange={onRecorderStateChange}
        onRecordingComplete={handleRecordingComplete}
        onSettingsClick={handleSettingsClick}
        onSettingsClose={handleSettingsClose}
        onResetClick={handleOnResetClick}
        onBackClick={handleBackClick}
        showBonjoroBadge={showBonjoroBadge}
        debug={process.env.APP_DEBUG === 'true'}
        onInit={handleVideoRecorderInit}
        mirrorClassName='ml-20'
      />
    </div>
  );
}
